// Typed CMS section model (schema_version 2). Source of truth for both the
// public renderer and the admin editor. The DB stores this as
// `cms_pages.metadata = { schema_version: 2, sections: CmsSection[] }`; Postgres
// only guards the coarse shape (object + schema_version + sections array), so
// these Zod schemas are the real validation layer (parse-tolerant on read).
//
// Field conventions (see the L6 plan):
//   - text fields are translated per locale ([i18n]);
//   - `id`, `type`, `visible`, `key`, `image_url`, `social` are shared across
//     the en/fr/ar rows of a slug ([shared]) — the migration assigns identical,
//     deterministic ids so the three rows stay structurally aligned.
import { z } from 'zod';

export const CMS_SCHEMA_VERSION = 2 as const;

export const FAQ_CATEGORIES = [
  'concept',
  'process',
  'price',
  'offPlan',
  'security',
  'payment',
] as const;
export type FaqCategory = (typeof FAQ_CATEGORIES)[number];

// Whitelisted link keys → mapped to app routes in `nav-routes.ts`. The admin
// never types a URL: it only toggles/reorders/relabels items with a known key.
export const NAV_KEYS = ['home', 'deals', 'about', 'faq', 'sell', 'contact'] as const;
export const FOOTER_LEGAL_KEYS = ['privacy', 'terms', 'cookies'] as const;

const baseFields = {
  id: z.string().min(1),
  visible: z.boolean(),
};

export const heroSectionSchema = z.object({
  ...baseFields,
  type: z.literal('hero'),
  kicker: z.string(),
  line1: z.string(),
  line2: z.string(),
  lead: z.string(),
  noAccount: z.string(),
  image_url: z.string(),
});

export const richTextSectionSchema = z.object({
  ...baseFields,
  type: z.literal('rich_text'),
  title: z.string(),
  html: z.string(),
});

export const imageSectionSchema = z.object({
  ...baseFields,
  type: z.literal('image'),
  image_url: z.string(),
  alt: z.string(),
  caption: z.string(),
});

export const definitionSectionSchema = z.object({
  ...baseFields,
  type: z.literal('definition'),
  kicker: z.string(),
  line1: z.string(),
  line2: z.string(),
  html: z.string(),
  readyDescription: z.string(),
  offPlanDescription: z.string(),
});

export const faqItemSchema = z.object({
  id: z.string().min(1),
  question: z.string(),
  answer: z.string(),
  category: z.enum(FAQ_CATEGORIES),
  visible: z.boolean(),
});
export const faqListSectionSchema = z.object({
  ...baseFields,
  type: z.literal('faq_list'),
  title: z.string(),
  lead: z.string(),
  items: z.array(faqItemSchema),
});

export const stepItemSchema = z.object({
  id: z.string().min(1),
  number: z.string(),
  title: z.string(),
  body: z.string(),
});
export const stepsListSectionSchema = z.object({
  ...baseFields,
  type: z.literal('steps_list'),
  kicker: z.string(),
  title: z.string(),
  steps: z.array(stepItemSchema),
});

export const pillarItemSchema = z.object({
  id: z.string().min(1),
  kicker: z.string(),
  title: z.string(),
  body: z.string(),
});
export const pillarsListSectionSchema = z.object({
  ...baseFields,
  type: z.literal('pillars_list'),
  kicker: z.string(),
  title: z.string(),
  items: z.array(pillarItemSchema),
});

export const ctaSectionSchema = z.object({
  ...baseFields,
  type: z.literal('cta'),
  kicker: z.string(),
  line1: z.string(),
  line2: z.string(),
  lead: z.string(),
});

export const navLinkItemSchema = z.object({
  id: z.string().min(1),
  key: z.enum(NAV_KEYS),
  label: z.string(),
  visible: z.boolean(),
});
export const navLinksSectionSchema = z.object({
  ...baseFields,
  type: z.literal('nav_links'),
  items: z.array(navLinkItemSchema),
});

export const footerLinkItemSchema = z.object({
  id: z.string().min(1),
  key: z.enum(NAV_KEYS),
  label: z.string(),
  visible: z.boolean(),
});
export const footerColumnSchema = z.object({
  id: z.string().min(1),
  heading: z.string(),
  links: z.array(footerLinkItemSchema),
});
export const footerColumnsSectionSchema = z.object({
  ...baseFields,
  type: z.literal('footer_columns'),
  columns: z.array(footerColumnSchema),
});

export const footerLegalItemSchema = z.object({
  id: z.string().min(1),
  key: z.enum(FOOTER_LEGAL_KEYS),
  label: z.string(),
  visible: z.boolean(),
});
export const footerLegalSectionSchema = z.object({
  ...baseFields,
  type: z.literal('footer_legal'),
  items: z.array(footerLegalItemSchema),
});

export const footerMetaSectionSchema = z.object({
  ...baseFields,
  type: z.literal('footer_meta'),
  tagline: z.string(),
  copyright: z.string(),
  social: z.object({
    instagram: z.string(),
    linkedin: z.string(),
    whatsapp: z.string(),
  }),
});

export const cmsSectionSchema = z.discriminatedUnion('type', [
  heroSectionSchema,
  richTextSectionSchema,
  imageSectionSchema,
  definitionSectionSchema,
  faqListSectionSchema,
  stepsListSectionSchema,
  pillarsListSectionSchema,
  ctaSectionSchema,
  navLinksSectionSchema,
  footerColumnsSectionSchema,
  footerLegalSectionSchema,
  footerMetaSectionSchema,
]);
export type CmsSection = z.infer<typeof cmsSectionSchema>;
export type CmsSectionType = CmsSection['type'];

export const cmsDocumentSchema = z.object({
  schema_version: z.literal(CMS_SCHEMA_VERSION),
  sections: z.array(cmsSectionSchema),
});
export type CmsDocument = z.infer<typeof cmsDocumentSchema>;

/**
 * Parse a raw `cms_pages.metadata` value into a typed document. Tolerant by
 * design: returns `null` for legacy/absent/invalid metadata so public callers
 * fall back to their i18n defaults instead of throwing.
 */
export function parseCmsDocument(metadata: unknown): CmsDocument | null {
  const result = cmsDocumentSchema.safeParse(metadata);
  return result.success ? result.data : null;
}

/**
 * Return the first section of a given type, narrowed to that variant, or
 * `undefined` when the document is null or has no such section. Public pages
 * use this to pull the one or two sections they render with bespoke layout.
 */
export function findSection<T extends CmsSection['type']>(
  doc: CmsDocument | null,
  type: T,
): Extract<CmsSection, { type: T }> | undefined {
  return doc?.sections.find((s): s is Extract<CmsSection, { type: T }> => s.type === type);
}
